import { BUSINESS_CRITICAL_MAX_AGE_MS } from '@module/mdrt-org/shared/utils/constants/business-critical-cache';

const MATCH_OPTIONS: CacheQueryOptions = { ignoreVary: true };

const WARM_CONCURRENCY = 8;

const readFresh = async (cache: Cache, url: string) => {
  const cached = await cache.match(url, MATCH_OPTIONS);
  const cachedAt = cached?.headers.get('date');

  if (!cachedAt || Date.now() - Date.parse(cachedAt) >= BUSINESS_CRITICAL_MAX_AGE_MS) {
    return undefined;
  }

  return cached;
};

// Resolves to the cached or freshly fetched response, or to undefined when the page could
// not be stored — a single unreachable page must never stop the rest of the pass.
export const warmPage = async ({
  cache,
  canReuse,
  headers,
  pathname,
  signal,
}: {
  cache: Cache;
  canReuse: boolean;
  headers: HeadersInit;
  pathname: string;
  signal: AbortSignal;
}): Promise<Response | undefined> => {
  try {
    const cached = canReuse ? await readFresh(cache, pathname) : undefined;

    if (cached) {
      return cached;
    }

    const response = await fetch(pathname, { headers, signal });

    if (!response.ok) {
      return undefined;
    }

    const copy = response.clone();
    await cache.put(pathname, response);

    return copy;
  } catch {
    return undefined;
  }
};

// Images are warmed the way an img element loads them (no-cors), which yields an opaque
// response the service worker can still replay offline. Opaque responses carry no date
// header, so they are only freshness-checked for same-origin subresources.
//
// The response is stored explicitly rather than left to the service worker's runtime
// caching: on a first launch the worker may not control this page yet, and an
// unintercepted fetch would warm nothing while the pass still reported success.
export const warmSubresource = async ({
  cache,
  isImage,
  signal,
  url,
}: {
  cache: Cache;
  isImage: boolean;
  signal: AbortSignal;
  url: string;
}) => {
  try {
    const cached = isImage ? await cache.match(url, MATCH_OPTIONS) : await readFresh(cache, url);

    if (cached) {
      return;
    }

    const response = await fetch(url, isImage ? { mode: 'no-cors', signal } : { signal });

    // A cross-origin image fetched with `no-cors` is opaque: its status is unavailable, so
    // `ok` is always false and it has to be accepted on type alone. Everything else has to
    // be a real success — storing a 4xx/5xx from the image optimizer would replay that
    // failure offline instead of retrying against the network later.
    if (response.ok || response.type === 'opaque') {
      await cache.put(url, response);
    }
  } catch {
    // Warming is best effort — a failed subresource just falls back to the network later.
  }
};

export const runPool = async <T>(items: T[], worker: (item: T) => Promise<void>) => {
  let cursor = 0;

  const runners = Array.from({ length: Math.min(WARM_CONCURRENCY, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      await worker(items[index]);
    }
  });

  await Promise.all(runners);
};

export const prune = async (cache: Cache, pathnames: string[]) => {
  const keys = await cache.keys();
  const wanted = new Set(pathnames.map((pathname) => new URL(pathname, location.origin).href));

  await Promise.all(keys.filter((key) => !wanted.has(key.url)).map((key) => cache.delete(key)));
};
